import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { AdminUser, Section, TempPasswordResult } from '@/lib/types';

/**
 * Admin console (route: /admin). Visible to admins only.
 *
 * Three panels:
 *   1. Users        — list, mint a new user, reset a password (temp password
 *                     is shown ONCE in a copyable box).
 *   2. Sections     — list + create.
 *   3. Membership   — edit a section's member list. A section with NO members
 *                     is visible to everyone; adding members restricts it.
 *
 * API contract (from @/lib/api):
 *   api.adminUsers()                          -> AdminUser[]
 *   api.createUser({email,name,isAdmin})     -> TempPasswordResult
 *   api.resetUserPassword(email)             -> TempPasswordResult
 *   api.sections()                        -> Section[]
 *   api.createSection({name})                -> Section
 *   api.getSectionMembers(sectionId)         -> string[]   (ASSUMED — see note)
 *   api.setSectionMembers(sectionId,{emails})-> { ok: true }
 */
export default function Admin() {
  const { user } = useAuth();

  if (!user?.isAdmin) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Not authorized
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          You need administrator access to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Admin
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Manage users, sections, and who can see what.
        </p>
      </header>

      <UsersPanel />
      <SectionsPanel />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared UI primitives                                                        */
/* -------------------------------------------------------------------------- */

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/50">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        {description && (
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
      {message}
    </p>
  );
}

/** A prominent, copyable box for a one-time temporary password. */
function TempPasswordBox({
  result,
  onDismiss,
}: {
  result: TempPasswordResult;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(result.tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [result.tempPassword]);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-600/60 dark:bg-amber-950/30">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Temporary password for {result.email}
          </p>
          <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300/90">
            Share this once — it will not be shown again.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
          aria-label="Dismiss"
        >
          &times;
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 select-all break-all rounded-md border border-amber-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 dark:border-amber-700/60 dark:bg-slate-900 dark:text-slate-100">
          {result.tempPassword}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Panel 1: Users                                                              */
/* -------------------------------------------------------------------------- */

function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mint form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [minting, setMinting] = useState(false);

  // The single-shot temp password result (from mint OR reset)
  const [tempResult, setTempResult] = useState<TempPasswordResult | null>(null);
  const [resettingEmail, setResettingEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.adminUsers();
      setUsers(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onMint = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !name.trim()) return;
      setMinting(true);
      setError(null);
      try {
        const result = await api.adminCreateUser({
          email: email.trim(),
          name: name.trim(),
          isAdmin,
        });
        setTempResult(result);
        setEmail('');
        setName('');
        setIsAdmin(false);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create user.');
      } finally {
        setMinting(false);
      }
    },
    [email, name, isAdmin, load],
  );

  const onReset = useCallback(async (userEmail: string) => {
    setResettingEmail(userEmail);
    setError(null);
    try {
      const result = await api.adminResetUser(userEmail);
      setTempResult(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reset password.',
      );
    } finally {
      setResettingEmail(null);
    }
  }, []);

  return (
    <Panel title="Users" description="Create accounts and reset passwords.">
      <div className="space-y-5">
        {tempResult && (
          <TempPasswordBox
            result={tempResult}
            onDismiss={() => setTempResult(null)}
          />
        )}

        {error && <ErrorNote message={error} />}

        {/* Mint user form */}
        <form
          onSubmit={onMint}
          className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40 sm:grid-cols-[1fr_1fr_auto]"
        >
          <div className="sm:col-span-2 sm:grid sm:grid-cols-2 sm:gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="person@settheroom.com"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                Name
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
          </div>
          <div className="flex items-center justify-between gap-4 sm:col-span-3">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900"
              />
              Administrator
            </label>
            <button
              type="submit"
              disabled={minting}
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 disabled:opacity-50 dark:focus:ring-offset-slate-800"
            >
              {minting ? 'Minting…' : 'Mint user'}
            </button>
          </div>
        </form>

        {/* Users list */}
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No users yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Role</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-slate-100 last:border-0 dark:border-slate-700/60"
                  >
                    <td className="py-2.5 pr-4 text-slate-900 dark:text-slate-100">
                      {u.display_name}
                    </td>
                    <td className="py-2.5 pr-4">
                      {u.is_admin ? (
                        <span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-900/50 dark:text-sky-300">
                          Admin
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">
                          Member
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500 dark:text-slate-400">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onReset(u.display_name)}
                        disabled={resettingEmail === u.display_name}
                        className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
                      >
                        {resettingEmail === u.display_name
                          ? 'Resetting…'
                          : 'Reset password'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Panels 2 & 3: Sections + Membership                                         */
/* -------------------------------------------------------------------------- */

function SectionsPanel() {
  const [sections, setSections] = useState<Section[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadSections = useCallback(async () => {
    const rows = await api.sections();
    setSections(rows);
    return rows;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [secs, us] = await Promise.all([api.sections(), api.adminUsers()]);
      setSections(secs);
      setUsers(us);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sections.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const onCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newName.trim()) return;
      setCreating(true);
      setError(null);
      try {
        await api.adminCreateSection(newName.trim());
        setNewName('');
        await loadSections();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to create section.',
        );
      } finally {
        setCreating(false);
      }
    },
    [newName, loadSections],
  );

  const selected = sections.find((s) => s.id === selectedId) ?? null;

  return (
    <>
      <Panel title="Sections" description="Top-level scopes for content.">
        <div className="space-y-5">
          {error && <ErrorNote message={error} />}

          <form onSubmit={onCreate} className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New section name"
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
            <button
              type="submit"
              disabled={creating}
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 disabled:opacity-50 dark:focus:ring-offset-slate-800"
            >
              {creating ? 'Creating…' : 'New section'}
            </button>
          </form>

          {loading ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading…
            </p>
          ) : sections.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No sections yet.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {sections.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between py-2.5"
                >
                  <span className="text-sm text-slate-900 dark:text-slate-100">
                    {s.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                      selectedId === s.id
                        ? 'bg-sky-600 text-white'
                        : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    {selectedId === s.id ? 'Editing members' : 'Edit members'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {selected && (
        <MembershipEditor
          key={selected.id}
          section={selected}
          users={users}
          onError={setError}
        />
      )}
    </>
  );
}

function MembershipEditor({
  section,
  users,
  onError,
}: {
  section: Section;
  users: AdminUser[];
  onError: (msg: string) => void;
}) {
  // Membership is keyed by email. AdminUser.display_name holds the email/login
  // in the current contract (see note in returned summary).
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // ASSUMED api: getSectionMembers(sectionId) -> string[] of emails.
        const members = (await api.adminGetSectionMembers(section.id)) as
          | string[]
          | { emails: string[] };
        const emails = Array.isArray(members) ? members : members.emails;
        if (!cancelled) setSelectedEmails(new Set(emails));
      } catch {
        // If the endpoint is missing, start from an empty selection rather
        // than blocking the editor.
        if (!cancelled) setSelectedEmails(new Set());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section.id]);

  const toggle = useCallback((email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.adminSetSectionMembers(section.id, Array.from(selectedEmails));
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      onError(
        err instanceof Error ? err.message : 'Failed to save members.',
      );
    } finally {
      setSaving(false);
    }
  }, [section.id, selectedEmails, onError]);

  const memberCount = selectedEmails.size;

  return (
    <Panel
      title={`Membership — ${section.name}`}
      description="A section with NO members is visible to everyone; add members to restrict it."
    >
      <div className="space-y-4">
        <p
          className={`rounded-md px-3 py-2 text-sm ${
            memberCount === 0
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
              : 'bg-slate-50 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300'
          }`}
        >
          {memberCount === 0
            ? 'No members selected — this section is visible to everyone.'
            : `Restricted to ${memberCount} member${
                memberCount === 1 ? '' : 's'
              }.`}
        </p>

        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No users to add.
          </p>
        ) : (
          <ul className="grid gap-1 sm:grid-cols-2">
            {users.map((u) => (
              <li key={u.id}>
                <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/40">
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(u.display_name)}
                    onChange={() => toggle(u.display_name)}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900"
                  />
                  <span>{u.display_name}</span>
                  {u.is_admin && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      (admin)
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 disabled:opacity-50 dark:focus:ring-offset-slate-800"
          >
            {saving ? 'Saving…' : 'Save members'}
          </button>
          {savedAt && (
            <span className="text-sm text-emerald-600 dark:text-emerald-400">
              Saved.
            </span>
          )}
        </div>
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
